import React, {
	memo,
	ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import _ from "lodash";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
	change,
	Field,
	formValueSelector,
	getFormInitialValues,
} from "redux-form";

import {
	AddressSearchInput,
	CustomDropdownByValue,
	CustomInput,
	SelectFormGroup,
} from "components";
import RadioButton from "components/radioButton";
import {
	AddressCoordinatesConstants,
	AddressSectionFields,
	GoogleAddressComponentsFields,
	GoogleAddressOptionFields,
} from "globalConstants";
import { checkIsLatOrLngManual, checkAddressFieldsEmpty } from "helpers";
import { TypeOfLocationValues, CountriesList } from "types";
import {
	addressValidation,
	alphaNumericNormalizer,
	checkAddressComponentsReduxErrors,
	matchesCityRegex,
	maxLength2,
	maxLength30,
	maxLength9,
	minLength2,
	minLength5,
	normalizeAll,
	onlyAlphanumericAndSpecialCharacters,
	onlyTwoUpperCaseLetters,
	required,
	trimMidSentenceNormalizer,
	trimNormalizer,
	validateLatLng,
	validateWhenTypeOfLocationIsOther,
} from "validators";

import AddressTextBlock from "./addressTextBlock";

import styles from "./addressSection.module.scss";

type AddressSectionProps = {
	formName: string;
	sectionName?: string;
	sectionSubName?: string;
	isLoading?: boolean;
	isRequired?: boolean;
	typeOfLocationValue: string;
	hasTypeOfLocation?: boolean;
	isAddressTheSameName?: string;
	isAddressTheSameLabel?: string;
	isAddressTheSameValue?: "true" | "false";
};

const typeOfLocationValues = TypeOfLocationValues.getOptions().map(item => ({
	id: item.value,
	name: item.displayName,
	value: item.name,
}));

const AddressSectionRedux = ({
	formName,
	sectionName,
	sectionSubName,
	isLoading = false,
	isRequired = false,
	typeOfLocationValue,
	hasTypeOfLocation = false,
	isAddressTheSameName,
	isAddressTheSameLabel,
	isAddressTheSameValue = "false",
}: AddressSectionProps) => {
	const dispatch = useDispatch();

	const [isManualMode, setIsManualMode] = useState<boolean>(false);

	const initialValuesSelector = getFormInitialValues(formName);
	const selector = formValueSelector(formName);

	const getCountryCode = state =>
		selector(
			state,
			`${sectionName}.${AddressSectionFields.CountryCode.name}`,
		);

	const getLat = state =>
		selector(state, `${sectionName}.${GoogleAddressOptionFields.Lat}`);
	const getLng = state =>
		selector(state, `${sectionName}.${GoogleAddressOptionFields.Lng}`);

	const initialValues = useSelector(initialValuesSelector);
	const countryCode = useSelector(getCountryCode);
	const lat = useSelector(getLat);
	const lng = useSelector(getLng);

	const getFieldName = useCallback(
		name =>
			sectionSubName
				? `${sectionSubName}.address.${name}`
				: `address.${name}`,
		[sectionSubName],
	);

	const handleSelect = useCallback(
		addressDetails => {
			const addressComponents =
				addressDetails?.[GoogleAddressOptionFields.Address];

			const streetNumber =
				addressComponents?.[
					GoogleAddressComponentsFields.StreetNumber.name
				] ?? "";

			const streetName =
				addressComponents?.[
					GoogleAddressComponentsFields.Street.name
				] ?? "";

			const street =
				streetNumber || streetName
					? `${streetNumber} ${streetName}`
					: "";

			const newAddressDetails = {
				...addressDetails,
				...addressComponents,
				[GoogleAddressComponentsFields.Street.name]: street,
			};

			delete newAddressDetails[GoogleAddressOptionFields.Address];
			dispatch(change(formName, sectionName || "", newAddressDetails));
			setIsManualMode(false);
		},
		[dispatch, formName, sectionName],
	);

	const handleCountryChange = useCallback(
		option => {
			dispatch(
				change(
					formName,
					`${sectionName || ""}.${
						AddressSectionFields.CountryCode.name
					}`,
					option.value,
				),
			);
		},
		[dispatch, formName, sectionName],
	);

	const getFieldComponent = useCallback(
		(component?: ReactNode) =>
			isManualMode
				? component || CustomInput
				: ({ input, label }) => (
						<AddressTextBlock
							value={input.value}
							label={`${label}: `}
						/>
				  ),
		[isManualMode],
	);

	const manualModeController = useCallback(
		isManual => {
			setIsManualMode(isManual);

			// Replace coordinates to indicate manual mode
			if (isManual) {
				dispatch(
					change(
						formName,
						`${sectionName || ""}.${GoogleAddressOptionFields.Lat}`,
						AddressCoordinatesConstants.ManualAddress.lat,
					),
				);
				dispatch(
					change(
						formName,
						`${sectionName || ""}.${GoogleAddressOptionFields.Lng}`,
						AddressCoordinatesConstants.ManualAddress.lng,
					),
				);
			}
		},
		[dispatch, formName, sectionName],
	);

	const addRequiredValidation = useCallback(
		(validations: Function[]) =>
			isRequired ? [required, ...validations] : [...validations],
		[isRequired],
	);

	const validationSearchField = useMemo(() => {
		if (!isManualMode) {
			if (isRequired) {
				return addRequiredValidation([
					validateLatLng(sectionName),
					checkAddressComponentsReduxErrors(
						sectionName,
						AddressSectionFields.SearchAddress.name,
					),
				]);
			} else {
				return checkAddressComponentsReduxErrors(
					sectionName,
					AddressSectionFields.SearchAddress.name,
				);
			}
		}
		return undefined;
	}, [addRequiredValidation, isManualMode, isRequired, sectionName]);

	const isAddressTheSame = isAddressTheSameValue === "true";

	useEffect(() => {
		// Set initial appearance of the component
		if (
			checkIsLatOrLngManual({ lat, lng }) &&
			!checkAddressFieldsEmpty(_.get(initialValues, sectionName || ""))
		) {
			setIsManualMode(true);
		}
	}, [initialValues, lat, lng, sectionName]);

	useEffect(() => {
		dispatch(
			change(
				formName,
				`${sectionName || ""}.${AddressSectionFields.Country.name}`,
				CountriesList.getByValue(countryCode),
			),
		);
	}, [
		countryCode,
		dispatch,
		formName,
		getFieldName,
		sectionName,
		sectionSubName,
	]);

	return (
		<>
			{isAddressTheSameName && (
				<Row className="mb-3">
					<Col md={12}>
						<div className="mb-3">{isAddressTheSameLabel}</div>
						<div className={styles.wrappRadio}>
							<Field
								component={RadioButton}
								type="radio"
								label="Yes"
								value="true"
								name={isAddressTheSameName}
								id={isAddressTheSameName}
								blurLoading={isLoading}
							/>
							<Field
								component={RadioButton}
								type="radio"
								label="No"
								value="false"
								name={isAddressTheSameName}
								id={`not-${isAddressTheSameName}`}
								blurLoading={isLoading}
							/>
						</div>
					</Col>
				</Row>
			)}

			{!isAddressTheSame && (
				<>
					<Row>
						<div id="myDiv"></div>
						<Col>
							<Field
								component={AddressSearchInput}
								label={
									AddressSectionFields.SearchAddress
										.displayName
								}
								validate={validationSearchField}
								name={getFieldName(
									AddressSectionFields.SearchAddress.name,
								)}
								showRequiredMark={!isManualMode && isRequired}
								smLabel={6}
								smInput={6}
								onSelect={handleSelect}
								isLoading={isLoading}
								labelClassName={styles.addressSearchLabel}
								inputClassName={styles.addressSearchInput}
								manualModeController={manualModeController}
							/>
						</Col>
					</Row>

					<Row className="mb-3">
						<Col lg={5}>
							<Field
								component={getFieldComponent()}
								name={getFieldName(
									AddressSectionFields.Street.name,
								)}
								label={`${AddressSectionFields.Street.displayName}`}
								validate={addRequiredValidation(
									addressValidation,
								)}
								normalize={normalizeAll([
									onlyAlphanumericAndSpecialCharacters,
									trimMidSentenceNormalizer,
								])}
								normalizeOnBlur={trimNormalizer}
								blurLoading={isLoading}
								requiredMark={isRequired}
							/>

							<Field
								component={getFieldComponent()}
								name={getFieldName(
									AddressSectionFields.City.name,
								)}
								label={`${AddressSectionFields.City.displayName}`}
								validate={addRequiredValidation([
									minLength2,
									maxLength30,
									matchesCityRegex,
								])}
								normalize={normalizeAll([
									onlyAlphanumericAndSpecialCharacters,
									trimMidSentenceNormalizer,
								])}
								normalizeOnBlur={trimNormalizer}
								blurLoading={isLoading}
							/>

							<Field
								component={getFieldComponent()}
								name={getFieldName(
									AddressSectionFields.Region.name,
								)}
								label={`${AddressSectionFields.Region.displayName}`}
								normalize={onlyTwoUpperCaseLetters}
								validate={addRequiredValidation([
									minLength2,
									maxLength2,
								])}
								blurLoading={isLoading}
							/>

							<Field
								component={getFieldComponent()}
								name={getFieldName(
									AddressSectionFields.PostalCode.name,
								)}
								label={`${AddressSectionFields.PostalCode.displayName}`}
								normalize={alphaNumericNormalizer}
								validate={addRequiredValidation([
									minLength5,
									maxLength9,
								])}
								blurLoading={isLoading}
							/>

							<Field
								component={getFieldComponent(SelectFormGroup)}
								name={getFieldName(
									AddressSectionFields.Country.name,
								)}
								label={`${AddressSectionFields.Country.displayName}`}
								options={CountriesList.getOptions()}
								getOptionLabel={o => o.displayName}
								getOptionValue={option => option}
								placeholder="Please Select"
								validate={addRequiredValidation([])}
								blurLoading={isLoading}
								onChange={handleCountryChange}
							/>
						</Col>
						<Col lg={{ span: 5, offset: 2 }}>
							<Field
								component={CustomInput}
								label={
									AddressSectionFields.Apartments.displayName
								}
								normalizeOnBlur={trimNormalizer}
								name={getFieldName(
									AddressSectionFields.Apartments.name,
								)}
								normalize={onlyAlphanumericAndSpecialCharacters}
								blurLoading={isLoading}
								hideRequiredMark
							/>
							{hasTypeOfLocation && (
								<Field
									component={CustomDropdownByValue}
									label={
										AddressSectionFields.TypeOfLocation
											.displayName
									}
									validate={isRequired ? required : undefined}
									selected={typeOfLocationValue}
									items={typeOfLocationValues}
									name={getFieldName(
										AddressSectionFields.TypeOfLocation
											.name,
									)}
									loading={isLoading}
								/>
							)}
							{hasTypeOfLocation &&
								typeOfLocationValue ===
									TypeOfLocationValues.Other.name && (
									<Field
										component={CustomInput}
										label={
											AddressSectionFields
												.OtherTypeOfLocation.displayName
										}
										validate={[
											validateWhenTypeOfLocationIsOther,
										]}
										normalizeOnBlur={trimNormalizer}
										name={getFieldName(
											AddressSectionFields
												.OtherTypeOfLocation.name,
										)}
										normalize={normalizeAll([
											onlyAlphanumericAndSpecialCharacters,
											trimMidSentenceNormalizer,
										])}
										blurLoading={isLoading}
									/>
								)}
						</Col>
					</Row>

					<Field
						component="input"
						type="hidden"
						name={getFieldName(GoogleAddressOptionFields.Lat)}
						disabled
					/>
					<Field
						component="input"
						type="hidden"
						name={getFieldName(GoogleAddressOptionFields.Lng)}
						disabled
					/>
				</>
			)}
		</>
	);
};

export default memo(AddressSectionRedux);
